//#region IMPORTS
import {
  ArrowDownTrayIcon,
  CodeBracketSquareIcon,
} from "@heroicons/react/24/outline";
import {
  fetchInstorePersoCountsAsync,
  instoreOrdersSelectors,
  requestPersoCheckStop,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { CHECK_PERSO_INSTORE_ORDERS } from "../../models/_consts";
import { exportPersoRowsToExcel } from "../../helpers/persoExcel";
import { format } from "date-fns";
import { toast } from "react-toastify";
import MainModal from "../_layout/MainModal";
//#endregion

// Perso check button (filter bar, next to "Archived only") + progress modal.
//
// Scope of a check:
//  - with a selection (workflow presets / export mode): exactly the selected
//    orders — "Check 7 selected for perso";
//  - without: every order loaded for the current preset — "Check all within
//    preset for perso".
// Both re-check orders that already have an answer. That is deliberate: the
// modal's Excel export covers the orders of the LAST run, so skipping known
// ones (an earlier per-row check, say) would silently drop them from "all".
// The modal shows progress, lets you Stop, and once done exports the perso
// lines of every checked order with perso to one Excel (helpers/persoExcel).
// Closing the modal does not stop the run; the button reopens it.
//
// Interim until the backend exposes `perso_lines_count` on the list — then the
// check itself goes; the exports stay (they fetch the detail on demand).
export default function InstoreOrdersPersoCheck() {
  //#region SETUP
  const dispatch = useAppDispatch();
  const orders = useAppSelector(instoreOrdersSelectors.selectAll);
  const selected = useAppSelector(
    (state) => state.instoreOrders.selectedInstoreOrders,
  );
  const persoLines = useAppSelector((state) => state.instoreOrders.persoLines);
  const run = useAppSelector((state) => state.instoreOrders.persoCheckRun);
  const running = !!run?.running;

  const hasSelection = selected.length > 0;
  const idsToCheck = hasSelection ? selected : orders.map((order) => order.id);
  //#endregion

  //#region LOGIC
  const openProgress = () => dispatch(openModal(CHECK_PERSO_INSTORE_ORDERS));

  const start = () => {
    if (idsToCheck.length === 0) return;
    openProgress();
    dispatch(fetchInstorePersoCountsAsync(idsToCheck));
  };

  const close = () => dispatch(openModal(false));

  // Export: the perso lines of every order in the last run that has some.
  const exportRows = (run?.ids ?? []).flatMap((id) => persoLines[id] ?? []);
  const exportToExcel = () => {
    if (exportRows.length === 0) return;
    try {
      exportPersoRowsToExcel(
        exportRows,
        `perso_export_${format(new Date(), "dd-MM-yyyy_HH-mm")}.xlsx`,
      );
      toast.success("Perso export downloaded");
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  const label = running
    ? "Checking perso…"
    : hasSelection
      ? `Check ${selected.length} selected for perso`
      : "Check all within preset for perso";

  const percent =
    run && run.total > 0 ? Math.round((run.done / run.total) * 100) : 0;
  //#endregion

  //#region RENDER
  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={running ? openProgress : start}
        disabled={!running && idsToCheck.length === 0}
        title={
          running
            ? "Show progress"
            : hasSelection
              ? "Check the selected orders for perso lines"
              : "Check every order in this list for perso lines"
        }
        className="inline-flex items-center gap-x-1 rounded bg-white px-2 py-1 text-sm font-semibold text-purple-900 shadow-sm ring-1 ring-inset ring-purple-300 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
      >
        <CodeBracketSquareIcon
          className={running ? "h-4 w-4 animate-pulse" : "h-4 w-4"}
          aria-hidden="true"
        />
        {label}
      </button>

      <MainModal type={CHECK_PERSO_INSTORE_ORDERS} closeHandler={close}>
        <div className="text-center">
          <h3 className="text-base font-semibold text-gray-900">
            Checking orders for perso
          </h3>

          <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-purple-600 transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>

          <p className="mt-2 text-sm font-semibold text-gray-900">
            {run?.done ?? 0} / {run?.total ?? 0} orders checked
          </p>
          <p className="text-sm text-gray-500">
            {run?.found ?? 0} with perso
            {run && run.failed > 0
              ? ` · ${run.failed} could not be loaded`
              : ""}
          </p>
          {run && !run.running && run.stopRequested && (
            <p className="mt-1 text-xs text-gray-400">
              Stopped — the remaining orders keep their "check" button.
            </p>
          )}

          <div className="mt-6 flex justify-center gap-3">
            {running ? (
              <button
                type="button"
                onClick={() => dispatch(requestPersoCheckStop())}
                disabled={!!run?.stopRequested}
                className="inline-flex items-center rounded bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {run?.stopRequested ? "Stopping…" : "Stop"}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={exportToExcel}
                  disabled={exportRows.length === 0}
                  title={
                    exportRows.length === 0
                      ? "No perso lines found in the checked orders"
                      : `Export ${exportRows.length} perso line${
                          exportRows.length === 1 ? "" : "s"
                        } to Excel`
                  }
                  className="inline-flex items-center gap-x-1 rounded bg-purple-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-purple-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                  Export perso to Excel
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex items-center rounded bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </MainModal>
    </div>
  );
  //#endregion
}
